import type {
  NarrativeAgentWorkEvent,
  NarrativeTranscriptEntry,
  NarrativeWorkspaceLink,
  NarrativeWorkspaceSession,
} from "../components/narrative/NarrativeSessionWorkspace";
import type { NarrativeSessionSummary } from "../components/narrative/NarrativeSessionHistory";
import type {
  SensemakingResponse,
  SensemakingTimelineEdge,
  SensemakingTimelineNode,
} from "../components/narrative/sensemaking-types";
import { readBootstrapDraftState } from "./narrative-bootstrap-draft";
import {
  normalizeDatawrapperAsset,
  type ArticleDatawrapperAsset,
} from "../components/memorandums/assets/datawrapper-asset";

export interface NarrativeSessionBundle {
  session: NarrativeWorkspaceSession;
  response: SensemakingResponse | null;
}

export function requireSession(
  session: RawSessionDetail | undefined,
): RawSessionDetail {
  if (!session) throw new Error("Narrative session response was empty");
  return session;
}

export function toSessionSummary(session: RawSession): NarrativeSessionSummary {
  const catalystIds = toCatalystIds(
    session.catalystIds ?? session.catalyst_ids ?? session.catalysts,
  );
  const catalystCount =
    toNumber(session.catalystCount ?? session.catalyst_count) ||
    catalystIds.length;
  return {
    id: String(session.id),
    deskId: toOptionalString(
      session.deskId ?? session.desk_id ?? session.desk?.id,
    ),
    deskSlug: toOptionalString(
      session.deskSlug ?? session.desk_slug ?? session.desk?.slug,
    ),
    title: String(session.title ?? "Untitled narrative"),
    updatedAt: String(
      session.updatedAt ?? session.updated_at ?? new Date().toISOString(),
    ),
    catalystCount,
    catalystIds,
    color: String(session.color ?? "#c79f4a"),
    status: String(session.status ?? "active"),
    deskLabel: String(session.desk?.name ?? "Priced In Capital"),
  };
}

export function toSessionBundle(
  detail: RawSessionDetail,
): NarrativeSessionBundle {
  const response = toSensemakingResponse(detail);
  const bootstrapDraft = readBootstrapDraftState(detail.artifacts);
  return {
    response,
    session: {
      id: String(detail.id),
      title: String(detail.title ?? "Untitled narrative"),
      status: String(detail.status ?? "active"),
      color: String(detail.color ?? "#c79f4a"),
      generatedAt: String(
        detail.generatedAt ??
          detail.generated_at ??
          response?.generatedAt ??
          new Date().toISOString(),
      ),
      coverImageUrl: toOptionalString(
        detail.coverImageUrl ?? detail.cover_image_url,
      ),
      coverImagePrompt: toOptionalString(
        detail.coverImagePrompt ?? detail.cover_image_prompt,
      ),
      coverImageUpdatedAt: toOptionalString(
        detail.coverImageUpdatedAt ?? detail.cover_image_updated_at,
      ),
      catalystIds: toCatalystIds(detail.catalysts),
      bootstrapOpenerQuery: bootstrapDraft.openerQuery,
      report: readText(detail.artifacts?.docs?.payload, [
        "report",
        "summary",
        "title",
      ]),
      synthesis:
        response?.synthesisSummary ??
        readText(detail.artifacts?.docs?.payload, ["summary"]),
      webLinks: toLinks(detail),
      workEvents: toWorkEvents(detail.workEvents ?? detail.work_events),
      transcript: toTranscript(detail.messages),
      datawrapperFigures: toDatawrapperFigures(
        detail.artifacts?.docs?.payload?.datawrapperFigures,
      ),
    },
  };
}

function toDatawrapperFigures(value: unknown): ArticleDatawrapperAsset[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeDatawrapperAsset)
    .filter((figure): figure is ArticleDatawrapperAsset => Boolean(figure))
    .slice(0, 20);
}

function toCatalystIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") return item;
      if (!isRecord(item)) return "";
      return String(
        item.riskflow_item_id ??
          item.riskflowItemId ??
          item.catalyst_id ??
          item.catalystId ??
          item.id ??
          "",
      );
    })
    .filter(Boolean);
}

function toSensemakingResponse(
  detail: RawSessionDetail,
): SensemakingResponse | null {
  const flow = detail.artifacts?.flow?.payload;
  if (!flow) return null;
  const timeline = detail.artifacts?.timeline?.payload;
  return {
    anchorCatalysts: toArray(
      flow.anchorCatalysts,
    ) as unknown as SensemakingResponse["anchorCatalysts"],
    relatedCatalysts: toArray(
      flow.relatedCatalysts,
    ) as unknown as SensemakingResponse["relatedCatalysts"],
    narrativeGroups: toArray(
      flow.narrativeGroups,
    ) as unknown as SensemakingResponse["narrativeGroups"],
    timelineNodes: toArray(
      timeline?.nodes ?? flow.timelineNodes,
    ) as unknown as SensemakingTimelineNode[],
    timelineEdges: toArray(
      timeline?.edges ?? flow.timelineEdges,
    ) as unknown as SensemakingTimelineEdge[],
    synthesisSummary: String(flow.synthesisSummary ?? ""),
    forecast: isRecord(flow.forecast)
      ? {
          confidence: toNumber(flow.forecast.confidence),
          outcome: String(flow.forecast.outcome ?? ""),
          rationale: String(flow.forecast.rationale ?? ""),
        }
      : null,
    mermaidSource: String(flow.mermaidSource ?? ""),
    generatedAt: String(
      flow.generatedAt ??
        detail.generatedAt ??
        detail.generated_at ??
        new Date().toISOString(),
    ),
  };
}

function toLinks(detail: RawSessionDetail): NarrativeWorkspaceLink[] {
  const mapLink = (link: Record<string, unknown>) => ({
    label: String(link.title ?? link.label ?? link.url ?? "Report link"),
    href: String(link.url ?? link.href ?? ""),
    source: link.source ? String(link.source) : undefined,
    summary: toOptionalString(link.summary),
    imageUrl: toOptionalString(link.imageUrl ?? link.image_url),
    kind: toOptionalString(link.kind),
  });
  const linkedRows = toArray(detail.links).map(mapLink);
  const docLinks = toArray(detail.artifacts?.docs?.payload?.links).map(mapLink);
  return [...linkedRows, ...docLinks].filter((link) =>
    link.href.startsWith("http"),
  );
}

function toWorkEvents(value: unknown): NarrativeAgentWorkEvent[] {
  return toArray(value).map((event, index) => ({
    id: String(event.id ?? `work-${index}`),
    agent: String(event.agent ?? event.agent_name ?? "NarrativeFlow"),
    summary: String(event.summary ?? "Updated narrative workspace."),
    status: event.status
      ? String(event.status)
      : String(event.event_type ?? "complete"),
    timestamp: event.timestamp
      ? String(event.timestamp)
      : String(event.created_at ?? ""),
  }));
}

function toTranscript(value: unknown): NarrativeTranscriptEntry[] {
  return toArray(value).map((entry, index) => ({
    id: String(entry.id ?? `message-${index}`),
    speaker: String(entry.speaker ?? entry.role ?? "desk"),
    text: String(entry.text ?? entry.content ?? ""),
    timestamp: entry.timestamp
      ? String(entry.timestamp)
      : String(entry.created_at ?? ""),
  }));
}

function readText(value: unknown, keys: string[]): string | undefined {
  if (!isRecord(value)) return undefined;
  for (const key of keys) {
    const text = value[key];
    if (typeof text === "string" && text.trim()) return text;
  }
  return undefined;
}

function toArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function toNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function toOptionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

export interface RawSession extends Record<string, unknown> {
  id?: unknown;
  title?: unknown;
  color?: unknown;
  status?: unknown;
  desk?: { id?: unknown; name?: unknown; slug?: unknown };
}

export interface RawSessionDetail extends RawSession {
  artifacts?: Record<string, { payload?: Record<string, unknown> }>;
  catalysts?: unknown;
  messages?: unknown;
  workEvents?: unknown;
  work_events?: unknown;
  links?: unknown;
}
